import React, {
  createContext,
  FC,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import { useLazyQuery } from "@apollo/client";
import MpxService, {
  MpxAddressSuggestion,
  GeocodedAddress,
  BaseAddressDTO,
} from "@app/service/mpx";

interface SuggestAddressResponse {
  suggestAddress: MpxAddressSuggestion[];
}

interface GetAddressByPlaceIdResponse {
  getAddressByPlaceId: GeocodedAddress;
}

interface GeocodeAddressResponse {
  geocodeAddress: GeocodedAddress;
}

export interface MpxController {
  suggestAddress: (
    address: string
  ) => Promise<MpxAddressSuggestion[] | undefined>;
  getAddressByPlaceId: (
    placeId: string
  ) => Promise<GeocodedAddress | undefined>;
  geocodeAddress: (
    address: BaseAddressDTO
  ) => Promise<GeocodedAddress | undefined>;
  suggestAddressLoading: boolean;
  getAddressByPlaceIdLoading: boolean;
  geocodeAddressLoading: boolean;
}

export const Context = createContext<MpxController>({
  suggestAddress: () => Promise.reject(),
  getAddressByPlaceId: () => Promise.reject(),
  geocodeAddress: () => Promise.reject(),
  suggestAddressLoading: false,
  getAddressByPlaceIdLoading: false,
  geocodeAddressLoading: false,
});

Context.displayName = "MpxContext";

const MpxProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [
    suggestAddressQuery,
    { data: suggestAddressData, loading: suggestAddressLoading },
  ] = useLazyQuery<SuggestAddressResponse>(MpxService.suggestAddress(), {
    fetchPolicy: "no-cache",
  });

  const [getAddressByPlaceIdQuery, { loading: getAddressByPlaceIdLoading }] =
    useLazyQuery<GetAddressByPlaceIdResponse>(
      MpxService.getAddressByPlaceId(),
      {
        fetchPolicy: "no-cache",
      }
    );

  const [geocodeAddressQuery, { loading: geocodeAddressLoading }] =
    useLazyQuery<GeocodeAddressResponse>(MpxService.geocodeAddress(), {
      fetchPolicy: "no-cache",
    });

  const suggestAddress = useCallback(
    async (address: string): Promise<MpxAddressSuggestion[] | undefined> => {
      if (address.trim().length === 0) {
        return undefined;
      }

      try {
        const result = await suggestAddressQuery({
          variables: { address },
        });

        return result.data?.suggestAddress;
      } catch (error) {
        throw new Error(error as string);
      }
    },
    [suggestAddressQuery]
  );

  const getAddressByPlaceId = useCallback(
    async (placeId: string): Promise<GeocodedAddress | undefined> => {
      try {
        const result = await getAddressByPlaceIdQuery({
          variables: { placeId },
        });

        return result.data?.getAddressByPlaceId;
      } catch (error) {
        throw new Error(error as string);
      }
    },
    [getAddressByPlaceIdQuery]
  );

  const geocodeAddress = useCallback(
    async (address: BaseAddressDTO): Promise<GeocodedAddress | undefined> => {
      try {
        const result = await geocodeAddressQuery({
          variables: { address },
        });

        return result.data?.geocodeAddress;
      } catch (error) {
        throw new Error(error as string);
      }
    },
    [geocodeAddressQuery]
  );

  return (
    <Context.Provider
      value={{
        suggestAddress,
        getAddressByPlaceId,
        geocodeAddress,
        suggestAddressLoading,
        getAddressByPlaceIdLoading,
        geocodeAddressLoading,
      }}
    >
      {children}
    </Context.Provider>
  );
};

export default MpxProvider;
